'use client';

import { type ReactNode, useEffect } from 'react';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { useCreation } from '@ps/functions/hooks/useCreation';
import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { WithID } from '@ps/models/lib/base';
import type { PrivateProfile } from '@ps/models/lib/private-profile';
import type { Profile } from '@ps/models/lib/profile';
import type { ReadonlyProfile } from '@ps/models/lib/readonly-profile';
import { BehaviorSubject, EMPTY, Observable, of, switchMap } from 'rxjs';

import { useAuthContext } from '../auth';
import { UserProfileContext, UserProfileContextValue } from './context';

function createDocObservable<T>(
  collectionName: string,
  userId: string,
): Observable<WithID<T> | undefined> {
  return new Observable(subscriber => {
    let unsubscribe: (() => void) | undefined;
    let disposed = false;

    // Lazy-import firestore so `firebase/firestore` stays OUT of the first-load
    // bundle. This provider is mounted on the public-bio visitor routes (via the
    // `@modal` parallel slot), where no user is signed in — the import (and the
    // ~390KB firestore SDK) only loads once an authenticated userId actually
    // drives a subscription. Mirrors the deferred-auth pattern.
    void (async () => {
      const { doc, firestore, onSnapshot } =
        await import('@ps/functions/firebase/firestore');
      if (disposed) {
        return;
      }

      const firestoreInstance = firestore();
      if (!firestoreInstance) {
        subscriber.next(undefined);
        subscriber.complete();
        return;
      }

      const documentRef = doc(firestoreInstance, collectionName, userId);
      unsubscribe = onSnapshot(
        documentRef,
        snapshot => {
          subscriber.next(snapshotToDataWithId<T>(snapshot));
        },
        error => {
          console.error(`Error fetching ${collectionName}/${userId}:`, error);
          subscriber.next(undefined);
        },
      );

      // Torn down while the dynamic import was still in flight.
      if (disposed) {
        unsubscribe();
      }
    })();

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  });
}

type Props = {
  /**
   * Server-resolved profile to SSR-seed the provider with, so the first client
   * paint has the profile without waiting on the firestore stream. The live
   * `onSnapshot` subscription overwrites it once it resolves. Passed by
   * `WithCreatorDashboard`; omitted elsewhere (subject starts `undefined`).
   */
  initialProfile?: WithID<Profile>;
  children?: ReactNode;
};

export function UserProfileProvider({ initialProfile, children }: Props) {
  const { currentUserId$ } = useAuthContext();

  const currentUserProfile$ = useCreation(
    () => new BehaviorSubject<WithID<Profile> | undefined>(initialProfile),
    [],
  );

  const currentUserPrivateProfile$ = useCreation(
    () => new BehaviorSubject<WithID<PrivateProfile> | undefined>(undefined),
    [],
  );

  const currentUserReadonlyProfile$ = useCreation(
    () => new BehaviorSubject<WithID<ReadonlyProfile> | undefined>(undefined),
    [],
  );

  const [currentUserProfile] = useBehaviorSubject(currentUserProfile$);
  const [currentUserPrivateProfile] = useBehaviorSubject(
    currentUserPrivateProfile$,
  );
  const [currentUserReadonlyProfile] = useBehaviorSubject(
    currentUserReadonlyProfile$,
  );

  useEffect(() => {
    const subscription = currentUserId$
      .pipe(
        switchMap(userId => {
          // Lazy Firebase auth is TRI-STATE: `undefined` = still resolving,
          // `null` = confirmed anonymous, uid = signed in. While resolving we
          // must NOT emit — pushing `of(undefined)` here would blank a
          // server-seeded profile and flash the gated dashboard. Wait for a
          // definite uid (stream it) or `null` (clear to undefined).
          if (userId === undefined) return EMPTY;
          return userId
            ? createDocObservable<Profile>('profiles', userId)
            : of(undefined);
        }),
      )
      .subscribe(profile => {
        currentUserProfile$.next(profile);
      });

    return () => subscription.unsubscribe();
  }, [currentUserId$, currentUserProfile$]);

  useEffect(() => {
    const subscription = currentUserId$
      .pipe(
        switchMap(userId => {
          // Skip the resolving window (see the profile effect above).
          if (userId === undefined) return EMPTY;
          return userId
            ? createDocObservable<PrivateProfile>('privateProfiles', userId)
            : of(undefined);
        }),
      )
      .subscribe(privateProfile => {
        currentUserPrivateProfile$.next(privateProfile);
      });

    return () => subscription.unsubscribe();
  }, [currentUserId$, currentUserPrivateProfile$]);

  useEffect(() => {
    const subscription = currentUserId$
      .pipe(
        switchMap(userId => {
          // Skip the resolving window (see the profile effect above).
          if (userId === undefined) return EMPTY;
          return userId
            ? createDocObservable<ReadonlyProfile>('readonlyProfiles', userId)
            : of(undefined);
        }),
      )
      .subscribe(readonlyProfile => {
        currentUserReadonlyProfile$.next(readonlyProfile);
      });

    return () => subscription.unsubscribe();
  }, [currentUserId$, currentUserReadonlyProfile$]);

  const value: UserProfileContextValue = {
    currentUserProfile$,
    currentUserProfile,
    currentUserPrivateProfile$,
    currentUserPrivateProfile,
    currentUserReadonlyProfile$,
    currentUserReadonlyProfile,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}
